import { Dispatch, RefObject, SetStateAction, useCallback } from 'react';
import FocusLock from 'react-focus-lock';

import Portal from '@ui/portal/portal';
import ColorPicker from '@ui/color-picker/color-picker';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import useTrackBoundingClient from '@/utils/use-track-bounding-client';
import useDraggableElement from '@/utils/use-draggable-element';
import toastTrigger from '@ui/toast/toast-trigger';
import { IEventTag } from '@/types/projects.types';
import useClickAwayListener from '@/utils/use-click-away-listener';
import { TagOption, TOnMenuCloseOptions } from './tags-select.types';
import Container from '@ui/container/container';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import { useAppSelector } from '@/store/hooks';
import { selectIsSavingWorkingProject } from '@/store/slices/project/selectors';
import Text from '@ui/text/text/text';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';

type TTagsSelectColorPickerProps = {
  selectWrapperRef: RefObject<HTMLDivElement | null>;
  draggableRef: RefObject<HTMLDivElement | null>;
  displayColorPicker: IEventTag | null;
  setDisplayColorPicker: Dispatch<SetStateAction<IEventTag | null>>;
  workingTag: IEventTag;
  setWorkingTag: Dispatch<SetStateAction<IEventTag>>;
  selectedTags: TagOption[];
  onSave?: (selectedTags: IEventTag[], options?: TOnMenuCloseOptions) => void;
  presetColors?: string[];
};

const TagsSelectColorPicker = ({
  selectWrapperRef,
  draggableRef,
  displayColorPicker,
  setDisplayColorPicker,
  workingTag,
  setWorkingTag,
  selectedTags,
  onSave,
  presetColors,
}: TTagsSelectColorPickerProps) => {
  const savingProject = useAppSelector(selectIsSavingWorkingProject);

  const wrapperBoundingRect = useTrackBoundingClient({ ref: selectWrapperRef, open: !!displayColorPicker });

  useDraggableElement({
    ref: draggableRef,
    disabled: !displayColorPicker,
    dragHandleId: 'color-picker-drag-handle',
  });

  const handleClosePicker = useCallback(() => {
    setWorkingTag({
      name: '',
      color: '#000000',
      uuid: '',
    });

    setDisplayColorPicker(null);
  }, [setDisplayColorPicker, setWorkingTag]);

  useClickAwayListener({
    ref: draggableRef,
    open: !!displayColorPicker,
    onEscape: () => {
      handleClosePicker();
    },
  });

  const handleSaveColorChange = useCallback(() => {
    const updatedName = displayColorPicker?.name?.trim() || workingTag.name.trim();
    const nameExists = selectedTags.some((tag) => {
      if (tag.value === workingTag.uuid) return false;
      return tag.meta.name.trim().toLowerCase() === updatedName.toLowerCase();
    });
    if (nameExists) {
      toastTrigger(<div className="S2">Tag name already exists</div>, {
        type: 'error',
        toastId: 'tag-name-exists',
        closeOnClick: true,
      });
      return;
    }
    const updatedTags = selectedTags.map((t) => {
      if (t.value === workingTag.uuid) {
        return {
          ...t,
          meta: {
            ...t.meta,
            color: workingTag.color,
            name: updatedName,
          },
        };
      }
      return t;
    });

    setDisplayColorPicker(null);

    onSave?.(
      updatedTags.map((t) => t.meta),
      { forceSave: true }
    );
  }, [displayColorPicker?.name, onSave, selectedTags, setDisplayColorPicker, workingTag]);

  return (
    <Portal>
      <FocusLock disabled={!displayColorPicker}>
        <div
          ref={draggableRef}
          onClick={(e) => e.stopPropagation()}
          style={{
            transitionProperty: 'opacity, visibility, pointer-events, z-index, transform',
            transitionDuration: '250ms, 0ms, 0ms, 0ms, 0ms',
            transitionDelay: '50ms, 0ms, 0ms, 0ms, 0ms',
            opacity: displayColorPicker ? 1 : 0,
            position: 'fixed',
            zIndex: displayColorPicker ? 1000 : -1,
            pointerEvents: displayColorPicker ? 'auto' : 'none',
            visibility: displayColorPicker ? 'visible' : 'hidden',
            top: 0,
            left: 0,
            transform: `translate(${(wrapperBoundingRect?.left || 0) - (draggableRef.current?.offsetWidth || 2 / 2)}px, ${wrapperBoundingRect?.top}px)`,
          }}
        >
          <Container
            background="var(--gray-900)"
            width="322px"
            height="56px"
            id="color-picker-drag-handle"
            borderRadius="12px"
            className="flex fd-row jc-space-between ai-flex-end padding-h-4 padding-b-8 gap-4"
          >
            <InputWithOverlay
              inputHeight="32px"
              wrapperClass="width-100-percent"
              inputProps={{
                className: 'clear-input padding-0 radius-4',
                value: displayColorPicker?.name || '',
                onChange: (e) => {
                  const value = e.target.value;
                  setDisplayColorPicker((prev) => {
                    if (!prev) return null;
                    return {
                      ...prev,
                      name: value,
                    };
                  });
                },
              }}
            />
            <div className="flex jc-center ai-flex-end gap-4">
              <Button
                designSize="small"
                loading={savingProject}
                hoverOpacity={0.8}
                onClick={handleSaveColorChange}
                disabled={
                  savingProject ||
                  (displayColorPicker?.color === workingTag.color &&
                    displayColorPicker?.name?.trim() === workingTag.name?.trim())
                }
                backgroundColor={getThemeColorCSSVariable()}
                className="flex ai-center"
              >
                <Text
                  minWidth="max-content"
                  color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
                  className="S2"
                >
                  Save
                </Text>
              </Button>
              <Button
                designSize="small"
                className="square-28 padding-h-4 padding-v-0 radius-4 flex ai-center jc-center"
                onClick={handleClosePicker}
                backgroundColor="transparent"
                hoverBackgroundColor="var(--gray-600"
              >
                <div>
                  <Icon name={ICONS.CLOSE} size={16} />
                </div>
              </Button>
            </div>
          </Container>

          <ColorPicker
            key={displayColorPicker?.uuid}
            initialColor={displayColorPicker?.color}
            presetColors={presetColors}
            onChange={(color) => {
              setWorkingTag((prev) => ({
                ...prev,
                color: color.hex,
              }));
            }}
          />
        </div>
      </FocusLock>
    </Portal>
  );
};

export default TagsSelectColorPicker;
